/**
 * WP8 test hook. Installs `window.__pitTest` giving E2E tests direct,
 * deterministic access to the WS client + store state — bypassing
 * canvas hit-testing and pointer coordinates that are impossible to
 * drive reliably against a Pixi WebGL surface in headless CI.
 *
 * Enabled ONLY when `window.location.search` contains `pitTest=1`.
 * Every production visit is a no-op; the hook is dead code otherwise.
 * The e2e Playwright spec opens URLs with `?pitTest=1` and then talks
 * to `window.__pitTest.*` directly for `sendInput`, `createMatch`,
 * `joinMatch`, phase/winner reads, latest snapshot, hp reads, etc.
 *
 * IMPORTANT (WP8 traps):
 *   • This file is imported unconditionally, but the install function
 *     early-returns unless the query string opts in. There is NO way
 *     to enable this in prod without visibly appending `?pitTest=1` to
 *     the URL.
 *   • The hook holds live references to the WsClient + the latest
 *     dispatched snapshot; assertions in Playwright poll it via
 *     `page.evaluate(() => window.__pitTest.getState())` which is
 *     deterministic (no real-time-sampled sleep needed).
 */

import { toChampionId } from "@pit/protocol";
import type { MatchId, SimInput, WireSimState, MatchPhase, TeamIndex } from "@pit/protocol";
import type { WsClient } from "./net/WsClientProvider.js";
import type { StoreState } from "./store/types.js";
import { DEFAULT_CHAMPION_ID } from "./champions/catalog.js";

export interface PitTestHookState {
  readonly matchId: string | null;
  readonly joinUrl: string | null;
  readonly ownSeat: number | null;
  readonly phase: MatchPhase;
  readonly winner: TeamIndex | null;
  readonly connectionKind: string;
  readonly snapshot: WireSimState | null;
}

export interface PitTestHook {
  readonly enabled: true;
  /** Optional champion id (defaults to the catalog default). */
  createMatch(championId?: string): void;
  joinMatch(id: string, championId?: string): void;
  sendInput(input: SimInput): void;
  getState(): PitTestHookState;
  /** Return hp for a seat from the latest sampled snapshot (or null). */
  hpFor(seat: number): number | null;
  /** Return entity id for a seat (or null). */
  entityIdFor(seat: number): string | null;
}

declare global {
  interface Window {
    __pitTest?: PitTestHook;
  }
}

let latestState: StoreState | null = null;
let latestClient: WsClient | null = null;

/** Called by WsClientProvider each render with the current state + client. */
export function pushTestHookRefs(state: StoreState, client: WsClient): void {
  if (!isTestModeEnabled()) return;
  latestState = state;
  latestClient = client;
  installIfNeeded();
}

function isTestModeEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("pitTest") === "1";
  } catch {
    return false;
  }
}

function installIfNeeded(): void {
  if (typeof window === "undefined") return;
  if (window.__pitTest !== undefined) return;
  window.__pitTest = {
    enabled: true,
    createMatch(championId?: string): void {
      // #323: e2e drives no loadout customization — `null` resolves
      // server-side to the champion's full ability pool (today's only
      // legal loadout for every shipped champion).
      latestClient?.createMatch(
        championId === undefined ? DEFAULT_CHAMPION_ID : toChampionId(championId),
        null,
      );
    },
    joinMatch(id: string, championId?: string): void {
      latestClient?.joinMatch(
        id as unknown as MatchId,
        championId === undefined ? DEFAULT_CHAMPION_ID : toChampionId(championId),
        null,
      );
    },
    sendInput(input: SimInput): void {
      latestClient?.sendInput(input);
    },
    getState(): PitTestHookState {
      const s = latestState;
      if (s === null) {
        return {
          matchId: null,
          joinUrl: null,
          ownSeat: null,
          phase: "waiting",
          winner: null,
          connectionKind: "idle",
          snapshot: null,
        };
      }
      return {
        matchId: s.matchId === null ? null : String(s.matchId),
        joinUrl: s.joinUrl,
        ownSeat: s.ownSeat,
        phase: s.phase.phase,
        winner: s.phase.winner,
        connectionKind: s.connection.kind,
        snapshot: s.hudSnapshot,
      };
    },
    hpFor(seat: number): number | null {
      const snap = latestState?.hudSnapshot ?? null;
      if (snap === null) return null;
      for (const ent of snap.entities) {
        if (Number(ent.seat) === seat) return ent.hp;
      }
      return null;
    },
    entityIdFor(seat: number): string | null {
      const snap = latestState?.hudSnapshot ?? null;
      if (snap === null) return null;
      for (const ent of snap.entities) {
        if (Number(ent.seat) === seat) return String(ent.entityId);
      }
      return null;
    },
  };
}
